import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Store, FileText, AlertCircle, CheckCircle2, Calculator, ArrowRight, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

export const DiscounterDrogerieSeite = () => {
  const objektmerkmale = [
    'Verkaufsfläche: Discounter 800-1.200 m², Drogerie 400-800 m²',
    'Grundstücksgröße: 2.500-5.000 m² inkl. Kundenparkplätze',
    'Mieter: Aldi, Lidl, Netto, Penny (Discounter) | dm, Rossmann, Müller (Drogerie)',
    'Standort: Wohngebiete, Ortszentren, frequentierte Lagen',
    'Bauweise: Standardisiert, funktional, geringer Investitionsbedarf'
  ];

  const kaeufergruppen = [
    {
      gruppe: 'Institutionelle (v.a. bei Discountern)',
      erwartungen: 'WAULT über 8 Jahre, Aldi/Lidl bevorzugt (höchste Bonität), Indexierung, Mindestvolumen 2-3 Mio. €',
      rendite: '4,0-5,0%'
    },
    {
      gruppe: 'Family Offices',
      erwartungen: 'WAULT über 6 Jahre, stabile Mieter, auch Drogerien interessant, flexibles Volumen',
      rendite: '4,5-5,5%'
    },
    {
      gruppe: 'Privatinvestoren',
      erwartungen: 'WAULT über 5 Jahre, auch kleinere Objekte (Drogerien), Fokus auf Rendite',
      rendite: '5,0-6,5%'
    }
  ];

  const unterlagen = [
    'Mietvertrag mit allen Nachträgen (bei Discountern oft standardisiert)',
    'Betriebskostenabrechnungen letzte 3 Jahre',
    'Grundbuchauszug (nicht älter als 3 Monate)',
    'Flurkarte und Lageplan mit Parkplatzsituation',
    'Energieausweis',
    'Baugenehmigung',
    'Nachweis aktuelle Mietzahlungen (letzte 12 Monate)',
    'Fotos Außen- und Innenansichten'
  ];

  const fehler = [
    {
      fehler: 'Zu kurze Restlaufzeit unterschätzt',
      folge: 'Preis deutlich unter Erwartung, weniger Käuferinteresse',
      vermeidung: 'Bereits 2-3 Jahre vor Vertragsende mit Mieter über Verlängerung sprechen'
    },
    {
      fehler: 'Parkplatzsituation unklar',
      folge: 'Käufer haben Bedenken wegen künftiger Vermietbarkeit',
      vermeidung: 'Parkplatzanzahl dokumentieren, ggf. Vermessung durch Sachverständigen'
    },
    {
      fehler: 'Betriebskosten zu hoch',
      folge: 'Reduziert Nettomiete und damit Verkaufspreis',
      vermeidung: 'Vor Verkauf Betriebskosten optimieren, nicht umlagefähige Kosten reduzieren'
    }
  ];

  const faq = [
    {
      q: 'Warum sind Discounter bei Investoren so beliebt?',
      a: 'Aldi und Lidl gehören zu den bonitätsstärksten Mietern in Deutschland (AAA-Rating). Standardisierte Mietverträge, lange Laufzeiten (meist 12-15 Jahre), triple-net (Mieter trägt alle Kosten), krisensicheres Geschäftsmodell. Bei WAULT über 10 Jahren sind Verkaufspreise mit Faktor 20-25 der Jahresmiete möglich.'
    },
    {
      q: 'Was bedeutet Triple Net bei Discounter-Mietverträgen?',
      a: 'Bei Triple-Net-Verträgen trägt der Mieter ALLE Kosten: Instandhaltung, Reparaturen, Versicherungen, Grundsteuer, Betriebskosten. Für Eigentümer bedeutet das: planbare Nettorendite, geringes operatives Risiko. Für Käufer ist das sehr attraktiv.'
    },
    {
      q: 'Welche Renditen sind aktuell am Markt erzielbar?',
      a: 'Core-Objekte (Aldi/Lidl, WAULT über 10 Jahre, 1a-Lage): 4,0-5,0% | Core+ (dm/Rossmann, WAULT 6-10 Jahre): 5,0-6,0% | Value-Add (kurze Laufzeiten, schlechtere Lage): 6,0-7,5%+. Renditen variieren stark nach Bonität, WAULT und Standort.'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Discounter & Drogerie Immobilie verkaufen – Aldi, Lidl, dm, Rossmann"
        description="Professionelle Vermittlung von Discounter- und Drogerie-Immobilien. Expertise zu WAULT, Triple-Net-Verträgen, Bewertung und Käufergruppen."
        url="/assetklassen/discounter-drogerie"
      />
      <Navbar />
      <main className="flex-grow">
        <section className="relative py-20 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6"></div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--navy-900)' }}>
                Discounter & Drogerie Immobilie verkaufen
              </h1>
              <p className="text-xl leading-relaxed mb-8" style={{ color: 'var(--gray-700)' }}>
                Professionelle Vermittlung von Discounter-Filialen (Aldi, Lidl, Netto) und 
                Drogeriemärkten (dm, Rossmann, Müller) mit höchster Mieter-Bonität.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="glass-card p-10">
              <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--navy-800)' }}>
                Was sind Discounter- und Drogerie-Immobilien?
              </h2>
              <p className="text-lg mb-6 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
                Diese Assetklasse umfasst Einzelhandelsimmobilien mit höchster Mieter-Bonität. 
                Discounter (Aldi, Lidl) und Drogerien (dm, Rossmann) gelten als krisensicherste Einzelhändler 
                mit stabilen Geschäftsmodellen.
              </p>
              
              {/* Marktdaten */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 p-6 bg-blue-50 rounded-lg">
                <div className="text-center">
                  <p className="text-3xl font-bold mb-1" style={{ color: 'var(--gold-600)' }}>~18.000</p>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Discounter + Drogerien in DE</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold mb-1" style={{ color: 'var(--gold-600)' }}>~950</p>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>m² Ø Verkaufsfläche</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold mb-1" style={{ color: 'var(--gold-600)' }}>12-15</p>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Jahre Ø Laufzeit</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold mb-1" style={{ color: 'var(--gold-600)' }}>4,5-5,5%</p>
                  <p className="text-sm" style={{ color: 'var(--gray-600)' }}>Typische Rendite</p>
                </div>
              </div>
              
              <h3 className="text-xl font-bold mb-4 mt-8" style={{ color: 'var(--navy-700)' }}>Typische Objektmerkmale:</h3>
              <ul className="space-y-3">
                {objektmerkmale.map((merkmal, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--gold-600)' }} />
                    <span style={{ color: 'var(--gray-700)' }}>{merkmal}</span>
                  </li>
                ))}
              </ul>

              {/* Regionale Verteilung */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--navy-700)' }}>Regionale Verteilung:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" style={{ color: 'var(--gray-700)' }}>
                  <div>
                    <strong>Discounter-Dichte:</strong> Flächendeckend in ganz Deutschland, besonders hohe Dichte in NRW, Bayern, Niedersachsen
                  </div>
                  <div>
                    <strong>Drogerien:</strong> Zentrale Lagen in Städten bevorzugt, dm mit über 2.000 Filialen marktführend
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Käufergruppen & deren Erwartungen
              </h2>
            </div>

            <div className="space-y-6">
              {kaeufergruppen.map((kg, index) => (
                <Card key={index} className="glass-card p-8 border-l-4" style={{ borderLeftColor: 'var(--gold-600)' }}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--navy-800)' }}>
                        {kg.gruppe}
                      </h3>
                      <div className="inline-block px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: 'var(--gold-100)', color: 'var(--gold-700)' }}>
                        Zielrendite: {kg.rendite}
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <p style={{ color: 'var(--gray-700)' }}>{kg.erwartungen}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Unterlagen-Checkliste */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Unterlagen-Checkliste
              </h2>
            </div>

            <Card className="glass-card p-8">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {unterlagen.map((dok, index) => (
                  <li key={index} className="flex items-start gap-3 p-3">
                    <FileText className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--navy-600)' }} />
                    <span className="text-sm" style={{ color: 'var(--gray-700)' }}>{dok}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </section>

        {/* Häufige Fehler */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Häufige Fehler vermeiden
              </h2>
            </div>

            <div className="space-y-6">
              {fehler.map((item, index) => (
                <Card key={index} className="glass-card p-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-5 w-5" style={{ color: 'var(--error-600)' }} />
                        <h3 className="font-bold" style={{ color: 'var(--error-600)' }}>Fehler:</h3>
                      </div>
                      <p style={{ color: 'var(--gray-800)' }}>{item.fehler}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--navy-700)' }}>Folge:</h4>
                      <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.folge}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2" style={{ color: 'var(--navy-700)' }}>Vermeidung:</h4>
                      <p className="text-sm" style={{ color: 'var(--gray-700)' }}>{item.vermeidung}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-4 mx-auto"></div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--navy-900)' }}>
                Häufig gestellte Fragen
              </h2>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {faq.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left font-semibold text-lg" style={{ color: 'var(--navy-700)' }}>
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed" style={{ color: 'var(--gray-700)' }}>
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="glass-card p-10 text-center">
              <Calculator className="h-16 w-16 mx-auto mb-6" style={{ color: 'var(--gold-600)' }} />
              <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--navy-800)' }}>
                WAULT berechnen
              </h2>
              <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: 'var(--gray-700)' }}>
                Nutzen Sie unseren kostenlosen WAULT-Rechner für eine erste Einschätzung.
              </p>
              <Link to="/tools/wault">
                <Button className="btn-gold-gradient text-white border-0 font-semibold px-10 py-7 text-lg shadow-lg">
                  Jetzt WAULT berechnen
                  <ArrowRight className="ml-2 h-6 w-6" />
                </Button>
              </Link>
            </Card>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Bereit Ihre Discounter-/Drogerie-Immobilie zu verkaufen?
            </h2>
            <p className="text-lg mb-8 opacity-90">
              Vereinbaren Sie ein unverbindliches Erstgespräch.
            </p>
            <Link to="/kontakt">
              <Button className="btn-gold-gradient text-white border-0 font-semibold px-10 py-7 text-lg shadow-2xl">
                Jetzt Kontakt aufnehmen
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DiscounterDrogerieSeite;